import React, { useRef, useEffect, useCallback, useState, MouseEvent } from 'react';
import Editor, { Monaco } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Stack,
  IconButton,
  Popover,
  Button
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { JSONSchema, MVELCompletionItem, MVELValidationResult } from './types';
import {
  MVEL_KEYWORDS,
  MVEL_FUNCTIONS,
  extractSchemaProperties,
  validateMVELSyntax
} from '@/src/utils/mvelUtils';
import lightTheme from '@/src/styles/themes/lightTheme';
import {
  createCompletionFromSchema,
  getLocalVariables,
  getLocalVariableType,
  getMethodsForType,
  getMonacoCompletionKind,
  getPropertiesForLocalVariable,
  getPropertiesForPath,
  getVariableTypeFromPath,
  mvelBuiltins,
  mvelLanguageConfig,
  mvelTokenProvider
} from '@/src/utils/mvelUtils/mvel-language-enhanced';
import { mvelEditorConfiguration } from '@/src/config/mvel/MvelEditorSchemas';
import { MVELSchema } from '@/src/types/mvel.types';
import RenderHelpText from '@/src/components/mvel-editor/RenderHelperText';

interface MVELCodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  inputSchema?: JSONSchema;
  onValidation?: (result: MVELValidationResult) => void;
  height?: string;
  readOnly?: boolean;
}

const MVELCodeEditor = (props: MVELCodeEditorProps) => {
  const { value, onChange, inputSchema, onValidation, height = '300px', readOnly = false } = props;

  const [monacoTheme, setMonacoTheme] = useState<string>('vs');
  const monacoRef = useRef<Monaco | null>(null);
  const editorRef = useRef<any>(null);
  const [errors] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const MVEL_LANGUAGE_ID = 'mvel';

  const setupCompletionProvider = useCallback(
    (monaco: Monaco) => {
      const schemaProperties = inputSchema ? extractSchemaProperties(inputSchema) : [];

      monaco.languages.registerCompletionItemProvider('mvel', {
        provideCompletionItems: (model, position) => {
          const word = model.getWordUntilPosition(position);
          const range = new monaco.Range(
            position.lineNumber,
            word.startColumn,
            position.lineNumber,
            word.endColumn
          );
          const suggestions = [
            // Schema properties
            ...schemaProperties.map((prop) => ({
              label: prop,
              kind: monaco.languages.CompletionItemKind.Property,
              insertText: prop,
              documentation: `Property from input schema: ${prop}`,
              range
            })),

            // MVEL keywords
            ...MVEL_KEYWORDS.map((keyword) => ({
              label: keyword,
              kind: monaco.languages.CompletionItemKind.Keyword,
              insertText: keyword,
              documentation: `MVEL keyword: ${keyword}`,
              range
            })),

            // MVEL functions
            ...MVEL_FUNCTIONS.map((func) => ({
              label: func,
              kind: monaco.languages.CompletionItemKind.Function,
              insertText: `${func}()`,
              documentation: `MVEL function: ${func}`,
              range
            }))
          ];

          return { suggestions };
        }
      });
    },
    [inputSchema]
  );

  const registerMVELLanguage = (monaco: typeof import('monaco-editor')) => {
    // Register language
    if (!monaco.languages.getLanguages().find((lang) => lang.id === MVEL_LANGUAGE_ID)) {
      monaco.languages.register({ id: MVEL_LANGUAGE_ID });
      monaco.languages.setLanguageConfiguration(MVEL_LANGUAGE_ID, mvelLanguageConfig);
      monaco.languages.setMonarchTokensProvider(MVEL_LANGUAGE_ID, mvelTokenProvider);
    }

    // Register completion provider
    monaco.languages.registerCompletionItemProvider(MVEL_LANGUAGE_ID, {
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn
        };

        const textUntilPosition = model.getValueInRange({
          startLineNumber: 1,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column
        });

        const suggestions: monaco.languages.CompletionItem[] = [];

        // Check if we're after a dot operator for property navigation
        const lineText = model.getLineContent(position.lineNumber);
        const beforeCursor = lineText.substring(0, position.column - 1);

        // Enhanced dot pattern matching to handle nested properties and local variables
        const dotMatch = beforeCursor.match(/(\w+(?:\.\w+)*)\.$/);

        if (dotMatch) {
          const propertyPath = dotMatch[1];
          const rootVariable = propertyPath.split('.')[0];

          // Check if it's an input schema property or local variable
          let properties: MVELCompletionItem[] = [];
          let variableType = 'any';

          if (inputSchema?.properties && inputSchema.properties[rootVariable]) {
            // It's from input schema
            properties = getPropertiesForPath(propertyPath, inputSchema);
            variableType = getVariableTypeFromPath(propertyPath, inputSchema);
          } else {
            // Check if it's a local variable defined in the code
            const localVarType = getLocalVariableType(rootVariable, textUntilPosition);
            if (localVarType !== 'unknown') {
              variableType = localVarType;
              // For local variables, provide basic type methods
              if (propertyPath === rootVariable) {
                properties = getPropertiesForLocalVariable(
                  rootVariable,
                  localVarType,
                  textUntilPosition
                );
              }
            }
          }

          // Add schema-based property suggestions
          properties.forEach((prop) => {
            suggestions.push({
              label: prop.label,
              kind: getMonacoCompletionKind(prop.kind, monaco),
              insertText: prop.insertText,
              detail: prop.detail,
              documentation: prop.documentation,
              range
            });
          });

          // Add type-specific methods
          const methods = getMethodsForType(variableType);
          methods.forEach((method) => {
            suggestions.push({
              label: method.label,
              kind: getMonacoCompletionKind(method.kind, monaco),
              insertText: method.insertText,
              insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              detail: method.detail,
              documentation: method.documentation,
              range
            });
          });
        } else {
          // Add MVEL built-ins when not after a dot
          mvelBuiltins.forEach((item) => {
            suggestions.push({
              label: item.label,
              kind: getMonacoCompletionKind(item.kind, monaco),
              insertText: item.insertText,
              insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              detail: item.detail,
              documentation: item.documentation,
              range
            });
          });

          // Add top-level input schema properties
          if (inputSchema?.properties) {
            const schemaCompletions = createCompletionFromSchema(inputSchema as MVELSchema);
            schemaCompletions.forEach((item) => {
              suggestions.push({
                label: item.label,
                kind: getMonacoCompletionKind(item.kind, monaco),
                insertText: item.insertText,
                detail: item.detail,
                documentation: item.documentation,
                range
              });
            });
          }

          // Add local variables defined in the code
          const localVars = getLocalVariables(textUntilPosition);
          localVars.forEach((varName) => {
            suggestions.push({
              label: varName,
              kind: monaco.languages.CompletionItemKind.Variable,
              insertText: varName,
              detail: 'Local variable',
              documentation: `Local variable: ${varName}`,
              range
            });
          });
        }

        return { suggestions };
      },
      triggerCharacters: ['.', ' ']
    });

    // Register hover provider
    monaco.languages.registerHoverProvider(MVEL_LANGUAGE_ID, {
      provideHover: (model, position) => {
        const word = model.getWordAtPosition(position);
        if (!word) return null;

        const hover = mvelBuiltins.find((item) => item.label === word.word);
        if (hover) {
          return {
            range: new monaco.Range(
              position.lineNumber,
              word.startColumn,
              position.lineNumber,
              word.endColumn
            ),
            contents: [
              { value: `**${hover.label}**` },
              { value: hover.documentation || hover.detail || '' }
            ]
          };
        }

        return null;
      }
    });
  };

  const handleEditorDidMount = (
    editor: monaco.editor.IStandaloneCodeEditor,
    monaco: typeof import('monaco-editor')
  ) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    registerMVELLanguage(monaco);

    // Configure editor options
    editor.updateOptions({
      fontSize: 14,
      fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace',
      lineNumbers: 'on',
      roundedSelection: false,
      scrollBeyondLastLine: false,
      minimap: { enabled: false },
      suggest: {
        snippetsPreventQuickSuggestions: false
      }
    });
  };

  const handleChange = useCallback(
    (value: string = '') => {
      // syntax validation
      const validation = validateMVELSyntax(value);

      // Dynamic output shape check based on schema's required fields
      const requiredFields: string[] =
        (mvelEditorConfiguration.MVEL_OUTPUT_SCHEMA as any)?.required ?? [];

      if (requiredFields.length > 0) {
        // Find the return statement with an object block
        const returnRegex = /return\s*{([\s\S]*?)}/;
        const returnMatch = value.match(returnRegex);

        if (!returnMatch) {
          validation.errors.push(
            `Missing return statement. User should return {${requiredFields.join(', ')}}`
          );
        } else {
          const returnObjectContent = returnMatch[1];
          const missingFields = requiredFields.filter((field) => {
            // Check for field as a key in the return object (e.g., "isAllowed": or 'isAllowed': or isAllowed:)
            const fieldRegex = new RegExp(`(?:["']${field}["']|\\b${field}\\b)\\s*:`);
            return !fieldRegex.test(returnObjectContent);
          });

          if (missingFields.length > 0) {
            validation.errors.push(`User should return {${requiredFields.join(', ')}}`);
          }
        }
      }
      if (monacoRef.current && editorRef.current) {
        const model = editorRef.current.getModel();
        if (model) {
          const markers =
            validation.errors.length > 0
              ? [
                  {
                    startLineNumber: 1,
                    startColumn: 1,
                    endLineNumber: Math.max(1, model.getLineCount()),
                    endColumn: model.getLineMaxColumn(model.getLineCount()),
                    message: validation.errors.join('\n'),
                    severity: monacoRef.current.MarkerSeverity.Error
                  }
                ]
              : [];
          monacoRef.current.editor.setModelMarkers(model, 'mvel-check', markers);
        }
      }

      onValidation?.({
        isValid: validation.errors.length === 0,
        errors: validation.errors
      });

      onChange?.(value);
    },
    [onChange, onValidation]
  );

  const handleChangeTheme = (themeValue: string) => {
    setMonacoTheme(themeValue === 'vs' ? 'vs-dark' : 'vs');
  };

  useEffect(() => {
    if (monacoRef.current && inputSchema) {
      setupCompletionProvider(monacoRef.current);
    }
  }, [inputSchema, setupCompletionProvider]);

  return (
    <Box
      sx={{
        border: '1px solid #ccc',
        borderRadius: '4px',
        overflow: 'hidden',
        flex: 1,
        width: '100%'
      }}
    >
      <Box
        sx={{
          backgroundColor: lightTheme.palette.grey[200],
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="subtitle2" paddingLeft={2}>
          MVEL Script Editor
        </Typography>
        <Box mr={2} display="flex" alignItems="center" columnGap={2}>
          <Button onClick={handleOpen} variant="outlined" sx={{ textTransform: 'none' }}>
            Input schema
          </Button>
          {monacoTheme === 'vs' ? (
            <IconButton onClick={() => handleChangeTheme('vs')}>
              <DarkModeIcon color="inherit" />
            </IconButton>
          ) : (
            <IconButton onClick={() => handleChangeTheme('vs-dark')}>
              <LightModeIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box sx={{ borderTop: '1px solid #e0e0e0' }}>
        <Editor
          height={height}
          defaultLanguage="mvel"
          value={value}
          onChange={handleChange}
          onMount={handleEditorDidMount}
          options={{
            theme: monacoTheme,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 14,
            lineNumbers: 'on',
            folding: true,
            automaticLayout: true,
            readOnly,
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: 'on',
            tabCompletion: 'on',
            wordBasedSuggestions: 'off'
          }}
        />
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        PaperProps={{
          sx: {
            pointerEvents: 'auto',
            p: 1,
            maxWidth: 400
          }
        }}
      >
        <RenderHelpText schema={mvelEditorConfiguration.MVEL_INPUT_SCHEMA} />
      </Popover>

      {errors.length > 0 && (
        <Box sx={{ borderTop: '1px solid #e0e0e0', p: 2, backgroundColor: '#fff8f8' }}>
          <Alert severity="error" variant="outlined">
            <AlertTitle>Syntax issues</AlertTitle>
            <Stack spacing={0.5}>
              {errors.map((err, idx) => (
                <Typography key={idx} variant="body2" color="error">
                  • {err}
                </Typography>
              ))}
            </Stack>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default MVELCodeEditor;
